import type { ReactNode } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { RouterOutputs } from "@/trpc";
import { CandidateViewKind, type CandidateViewKey } from "./candidate-views";

type NotInterestedReasons =
  RouterOutputs["jobPost"]["stats"]["notInterestedReasons"];

const NOT_SPECIFIED_REASON = "Not Specified";
const STAT_CARD_CLASS_NAME =
  "min-w-0 rounded-2xl border border-l-4 border-slate-200 bg-white p-4 text-left shadow-xs sm:p-5";

function StatCardBody({
  label,
  value,
  subLine,
  subLineClassName,
}: {
  label: string;
  value: number;
  subLine?: string;
  subLineClassName?: string;
}) {
  return (
    <>
      <span className="block text-sm font-medium text-slate-600">{label}</span>
      <span className="mt-1 block text-3xl font-semibold tabular-nums text-slate-900">
        {value}
      </span>
      {subLine && (
        <span
          className={cn("mt-1 block text-xs font-medium", subLineClassName)}
        >
          {subLine}
        </span>
      )}
    </>
  );
}

function SelectableStatCard({
  isSelected,
  onSelect,
  children,
}: {
  isSelected: boolean;
  onSelect: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={isSelected}
      onClick={onSelect}
      className={cn(
        STAT_CARD_CLASS_NAME,
        "transition-colors hover:border-slate-300 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary",
        isSelected ? "border-l-primary" : "border-l-slate-200",
      )}
    >
      {children}
    </button>
  );
}

function countReasons(notInterested: NotInterestedReasons) {
  const counts: Record<string, number> = {};
  for (const response of notInterested) {
    for (const { reason } of response.reasons) {
      if (reason) {
        counts[reason] = (counts[reason] ?? 0) + 1;
      }
    }
    if (response.reasons.length === 0) {
      counts[NOT_SPECIFIED_REASON] = (counts[NOT_SPECIFIED_REASON] ?? 0) + 1;
    }
  }
  return Object.entries(counts);
}

function NotInterestedReasonsPopover({
  notInterested,
}: {
  notInterested: NotInterestedReasons;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex min-h-11 items-center rounded-md px-1 text-sm text-slate-500 hover:text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary"
        >
          {notInterested.length} not interested ·{" "}
          <span className="ml-1 underline underline-offset-2">
            View reasons
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-96 max-w-[calc(100vw-2rem)] space-y-3 rounded-xl border-slate-200 bg-white p-4"
      >
        <p className="text-xs leading-5 text-slate-500">
          Note: Providers can select multiple reasons
        </p>
        <dl className="space-y-2">
          {countReasons(notInterested).map(([reason, count]) => (
            <div
              key={reason}
              className="flex items-start justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2 text-sm"
            >
              <dt className="text-slate-600 [overflow-wrap:anywhere]">
                {reason.charAt(0).toUpperCase() + reason.slice(1)}
              </dt>
              <dd className="font-semibold tabular-nums text-slate-900">
                {count}
              </dd>
            </div>
          ))}
        </dl>
      </PopoverContent>
    </Popover>
  );
}

export function JobDashboardStats({
  view,
  onSelectView,
  interestedCount,
  newCount,
  inProcessCount,
  hiredCount,
  notInterested,
}: {
  view: CandidateViewKey;
  onSelectView: (view: CandidateViewKey) => void;
  interestedCount: number;
  newCount: number;
  /** null when the job has no hiring stages, which hides the card. */
  inProcessCount: number | null;
  hiredCount: number;
  notInterested: NotInterestedReasons | undefined;
}) {
  return (
    <section aria-label="Job stats" className="space-y-1">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <SelectableStatCard
          isSelected={view === CandidateViewKind.All}
          onSelect={() => onSelectView(CandidateViewKind.All)}
        >
          <StatCardBody
            label="Interested"
            value={interestedCount}
            subLine={newCount > 0 ? `↑ ${newCount} new` : undefined}
            subLineClassName="text-emerald-600"
          />
        </SelectableStatCard>
        {inProcessCount !== null && (
          <div className={cn(STAT_CARD_CLASS_NAME, "border-l-slate-200")}>
            <StatCardBody
              label="In process"
              value={inProcessCount}
              subLine="Ongoing process"
              subLineClassName="text-slate-500"
            />
          </div>
        )}
        <SelectableStatCard
          isSelected={view === CandidateViewKind.Hired}
          onSelect={() => onSelectView(CandidateViewKind.Hired)}
        >
          <StatCardBody
            label="Hired"
            value={hiredCount}
            subLine="Offer accepted"
            subLineClassName="text-amber-600"
          />
        </SelectableStatCard>
      </div>
      {notInterested && notInterested.length > 0 && (
        <NotInterestedReasonsPopover notInterested={notInterested} />
      )}
    </section>
  );
}
